/**
 * How this app reaches a printer — the web half of `@rista/printing`'s ONE hardware port.
 *
 * Deliberately only transport: every decision about what prints, where and in what format was
 * made before a job gets here. See docs/flows/PRINTING.md §6.
 *
 *   Electron   bytes over IPC (`printer:print`, contentType RAW) — the main process answers
 *              `{success}|{error}` and already spaces jobs 500 ms apart
 *   NW.js      token text (the shell tokenizes it, as for every v1 print) or raster bytes,
 *              fire-and-forget exactly as v1 — the shell never answers a print
 *   LAN relay  token text posted to the master's local server, which prints on ITS default printer
 *   Android    the web build inside the RN wrapper: token text over `sendDataToApp`, which the
 *              native app prints. Images were already blanked by the document, because the host
 *              snapshot reports `canPrintImages: false` there — exactly what v1 did inline
 *
 * Still reported as UNSUPPORTED rather than guessed at: a plain browser tab (no printer bridge at
 * all; v1 silently did nothing), and the `builtIn` destination — a Pine Labs device inside the
 * Android wrapper, where v1 hands the whole SALE to the native app (`pinelabsPrintBill`) and lets
 * IT lay the receipt out, a protocol this package has no payload for. Everything that goes to a
 * PRINTER there still prints. flows/PRINTING.md §6.
 */
import type { Destination, Outcome, PayloadFormat, PrintJob, PrintTransport } from "@rista/printing";

import store from "redux-store/index";
import { exec, isAndroid, isElectron, isStandaloneWeb, sendDataToApp } from "utils/nativeHooks";
import { getLSRequestPayloadData, getMasterDomain } from "helper/localServer";
import { getVersionNumber } from "helper/versionHelper";
import {
  MIN_NW_APP_VERSION_SUPPORTED,
  NW_APP_EVENT_ACTIONS,
  NW_APP_EVENT_KEYS,
  NW_APP_LATEST_STABLE_VERSION,
  REACT_NATIVE_APP_EVENTS,
} from "data/constants";

const OK: Outcome = { ok: true };
const NONE: readonly PayloadFormat[] = [];
const BYTES: readonly PayloadFormat[] = ["escpos-bytes"];
const TEXT: readonly PayloadFormat[] = ["escpos-text"];
const TEXT_OR_BYTES: readonly PayloadFormat[] = ["escpos-text", "escpos-bytes"];

const electron = () => (window as any).electronAPI;
const isNwShell = () => !isElectron() && !isStandaloneWeb() && !isAndroid();

/** v1 `prepareLocalServerPrintData`: the master resolves its own default printer. */
function relayPayload(text: string) {
  const versionNumber = getVersionNumber();
  const modernShell =
    !isNaN(versionNumber) &&
    versionNumber >= MIN_NW_APP_VERSION_SUPPORTED.LS_BRIDGE &&
    versionNumber <= NW_APP_LATEST_STABLE_VERSION;
  const message = {
    domain: getMasterDomain(),
    tid: store.getState()?.businessStore?.businessData?.branchId,
    type: modernShell ? "PRINT_NEW" : "PRINT",
    data: [text, { remotePrinterId: "defaultPrinter" }],
  };
  return getLSRequestPayloadData("misc", message);
}

const fromIpc = (result: any): Outcome =>
  result?.error ? { ok: false, code: String(result.error) } : OK;

export const webTransport: PrintTransport = {
  accepts(destination: Destination) {
    if (destination.to === "relay") return isElectron() || isNwShell() ? TEXT : NONE;
    // a Pine Labs terminal's own printer: v1 sends it the whole sale, which has no payload here
    if (destination.to === "builtIn") return NONE;
    if (isElectron()) return BYTES;
    if (isNwShell()) return TEXT_OR_BYTES;
    // the Android wrapper prints what the native side is given
    if (isAndroid()) return TEXT;
    return NONE;
  },

  async send({ piece, payload }: PrintJob): Promise<Outcome> {
    if (piece.destination.to === "builtIn") return { ok: false, code: "UNSUPPORTED_PRINTER" };
    if (piece.destination.to === "relay") {
      if (payload.format !== "escpos-text") return { ok: false, code: "UNSUPPORTED_FORMAT" };
      const request = relayPayload(payload.text);
      if (isElectron()) return fromIpc(await electron().server.post(request));
      exec(NW_APP_EVENT_KEYS.server, NW_APP_EVENT_ACTIONS.server.post, request);
      return OK;
    }

    const { printer } = piece.destination;
    if (isAndroid()) {
      // 🔴 There is no drawer channel to the native app: v1's `openCashDrawer` reaches the drawer
      // through `window.electronAPI` or the NW shell's `exec` ONLY, so inside the wrapper it does
      // nothing at all. Sending the kick as a bill would PRINT "ESC p 0 25 250" on the receipt.
      // (A bill never plans one here — `planBill` reads `canPrintImages` — but `printCashDrawer`,
      // the header button, plans it on its own.)
      if (piece.document === "drawer") return { ok: false, code: "UNSUPPORTED_PRINTER" };
      // v1 `printSale`: `sendDataToApp({ eventName: printBill, data: printData, printerDetails })`,
      // fire-and-forget — the wrapper never answers a print either
      if (payload.format !== "escpos-text") return { ok: false, code: "UNSUPPORTED_FORMAT" };
      // v1 uses a DIFFERENT wrapper event for printer setup's test page than for a bill
      const eventName = piece.document === "testPrint" ? REACT_NATIVE_APP_EVENTS.printTestBill : REACT_NATIVE_APP_EVENTS.printBill;
      sendDataToApp({ eventName, data: payload.text, printerDetails: printer });
      return OK;
    }
    if (isElectron()) {
      if (payload.format !== "escpos-bytes") return { ok: false, code: "UNSUPPORTED_FORMAT" };
      return fromIpc(await electron().printer.print(payload.bytes, { ...printer, contentType: "RAW" }));
    }
    // `lines` is the Pine Labs payload; no web shell takes one, and `accepts` never offers it
    if (payload.format === "lines") return { ok: false, code: "UNSUPPORTED_FORMAT" };
    // NW: the shell's own tokenizer reads the printer's saved content type, exactly as in v1
    const args =
      payload.format === "escpos-text" ? [payload.text, printer] : [payload.bytes, { ...printer, contentType: "RAW" }];
    exec(NW_APP_EVENT_KEYS.printer, NW_APP_EVENT_ACTIONS.printer.print, args);
    return OK;
  },
};
